import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { availabilityService } from 'api/services/availabilityService';
import type { CategoryCalendar, InventoryDay } from 'api/types/availability.types';
import { addDays, toLocalDateStr, getDatesBetween, getOccupancyBadgeClass } from '../booking.utils';

// ============================================================================
// CELL COLOR MAP — inline styles to guarantee color application
// Bands map occupancy %: high (empty) → medium → low (busy) → critical → zero (full)
// ============================================================================

const CELL_BORDER = '1px solid #94a3b8';

const CELL_COLORS: Record<string, React.CSSProperties> = {
  high:     { backgroundColor: '#86efac', color: '#14532d', borderRight: CELL_BORDER, borderBottom: CELL_BORDER },
  medium:   { backgroundColor: '#fcd34d', color: '#78350f', borderRight: CELL_BORDER, borderBottom: CELL_BORDER },
  low:      { backgroundColor: '#fdba74', color: '#7c2d12', borderRight: CELL_BORDER, borderBottom: CELL_BORDER },
  critical: { backgroundColor: '#fca5a5', color: '#7f1d1d', borderRight: CELL_BORDER, borderBottom: CELL_BORDER },
  zero:     { backgroundColor: '#94a3b8', color: '#ffffff', textDecoration: 'line-through', borderRight: CELL_BORDER, borderBottom: CELL_BORDER },
};

// ============================================================================
// TYPES
// ============================================================================

interface AvailabilityCalendarPanelProps {
  propertyId: string;
  /** Stay window to highlight. Omit (dashboard use) to anchor on today with no highlight. */
  checkInDate?: string;
  checkOutDate?: string;
  /** Header label — defaults to the booking form's "Availability Overview". */
  title?: string;
  /**
   * Adds TOTAL FREE + Occupancy footer rows. The per-category cells answer a
   * reservations question ("can I sell a Deluxe on Friday?"); the front office
   * scanning the week thinks in house terms ("Thursday we're 62% full").
   * Off by default so the booking form renders byte-identical.
   */
  showTotals?: boolean;
  /** Bump to force a refetch (e.g. the workspace Refresh button). */
  refreshToken?: number;
}

interface CalendarData {
  categories: CategoryCalendar[];
  dayMap: Map<string, Map<string, InventoryDay>>;
}

const WINDOW_SIZE = 7;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getRequiredMonthsForRange = (start: Date, end: Date): { year: number; month: number }[] => {
  const months: { year: number; month: number }[] = [
    { year: start.getFullYear(), month: start.getMonth() + 1 },
  ];
  if (end.getMonth() !== start.getMonth() || end.getFullYear() !== start.getFullYear()) {
    months.push({ year: end.getFullYear(), month: end.getMonth() + 1 });
  }
  return months;
};

const formatHeaderLabel = (dateStr: string): { dayName: string; dateLabel: string } => {
  const d = new Date(dateStr + 'T00:00:00');
  return {
    dayName: DAY_NAMES[d.getDay()],
    dateLabel: `${String(d.getDate()).padStart(2, '0')} ${MONTH_NAMES[d.getMonth()]}`,
  };
};

const formatRangeLabel = (startStr: string, endStr: string): string => {
  const s = new Date(startStr + 'T00:00:00');
  const e = new Date(endStr + 'T00:00:00');
  const sd = `${String(s.getDate()).padStart(2, '0')} ${MONTH_NAMES[s.getMonth()]}`;
  const ed = `${String(e.getDate()).padStart(2, '0')} ${MONTH_NAMES[e.getMonth()]} ${e.getFullYear()}`;
  return `${sd} – ${ed}`;
};

// ============================================================================
// COMPONENT
// ============================================================================

const AvailabilityCalendarPanel: React.FC<AvailabilityCalendarPanelProps> = ({
  propertyId, checkInDate = '', checkOutDate = '', title = 'Availability Overview',
  showTotals = false, refreshToken,
}) => {
  const [calData, setCalData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(false);

  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const initialStart = useMemo(() => {
    if (checkInDate && checkInDate >= todayStr) return checkInDate;
    return todayStr;
  }, [checkInDate, todayStr]);

  const [windowStart, setWindowStart] = useState(initialStart);

  useEffect(() => {
    setWindowStart(initialStart);
  }, [initialStart]);

  const windowStartDate = useMemo(() => new Date(windowStart + 'T00:00:00'), [windowStart]);
  const windowEndDate = useMemo(() => addDays(windowStartDate, WINDOW_SIZE - 1), [windowStartDate]);
  const dateStrings = useMemo(() => getDatesBetween(windowStartDate, windowEndDate), [windowStartDate, windowEndDate]);

  const canGoPrev = windowStart > todayStr;

  const handlePrev = useCallback(() => {
    const prev = addDays(windowStartDate, -WINDOW_SIZE);
    const todayDate = new Date(todayStr + 'T00:00:00');
    const clampedStart = prev < todayDate ? todayDate : prev;
    setWindowStart(toLocalDateStr(clampedStart));
  }, [windowStartDate, todayStr]);

  const handleNext = useCallback(() => {
    const next = addDays(windowStartDate, WINDOW_SIZE);
    setWindowStart(toLocalDateStr(next));
  }, [windowStartDate]);

  const inRangeSet = useMemo(() => {
    if (!checkInDate || !checkOutDate) return new Set<string>();
    const lastNight = addDays(new Date(checkOutDate + 'T00:00:00'), -1);
    return new Set(getDatesBetween(new Date(checkInDate + 'T00:00:00'), lastNight));
  }, [checkInDate, checkOutDate]);

  const rangeLabel = useMemo(() => {
    if (dateStrings.length === 0) return '';
    return formatRangeLabel(dateStrings[0], dateStrings[dateStrings.length - 1]);
  }, [dateStrings]);

  // Fetch calendar data
  const fetchCalendar = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const months = getRequiredMonthsForRange(windowStartDate, windowEndDate);
      const responses = await Promise.all(
        months.map(m => availabilityService.getCalendar(propertyId, m.year, m.month))
      );

      const catMap = new Map<string, CategoryCalendar>();
      const dayMap = new Map<string, Map<string, InventoryDay>>();

      for (const res of responses) {
        if (!res.data?.categories) continue;
        for (const cat of res.data.categories) {
          if (!catMap.has(cat.roomCategoryId)) {
            catMap.set(cat.roomCategoryId, { ...cat, days: [] });
            dayMap.set(cat.roomCategoryId, new Map());
          }
          const catDays = dayMap.get(cat.roomCategoryId)!;
          for (const day of cat.days) {
            catDays.set(day.date.split('T')[0], day);
          }
        }
      }

      setCalData({ categories: Array.from(catMap.values()), dayMap });
    } catch {
      setCalData(null);
    } finally {
      setLoading(false);
    }
  }, [propertyId, windowStartDate, windowEndDate]);

  useEffect(() => {
    // refreshToken has no meaning here beyond re-running this effect — it lets
    // a parent (the FO workspace Refresh button) force a refetch.
    void refreshToken;
    if (propertyId) {
      fetchCalendar();
    }
  }, [fetchCalendar, propertyId, refreshToken]);

  if (!propertyId) return null;

  return (
    <div className="avail-cal-panel">
      {/* Section label */}
      <div className="avail-cal-header">
        <IconSystem.calendar size={14} />
        <span>{title}</span>
        {loading && <span className="avail-cal-loading">Loading...</span>}
      </div>

      {/* Navigation bar */}
      <div className="avail-cal-nav">
        <button
          type="button"
          className="avail-cal-nav-btn"
          onClick={handlePrev}
          disabled={!canGoPrev}
          title="Previous 7 days"
        >
          <IconSystem.chevronLeft size={14} />
        </button>
        <span className="avail-cal-nav-label">{rangeLabel}</span>
        <button
          type="button"
          className="avail-cal-nav-btn"
          onClick={handleNext}
          title="Next 7 days"
        >
          <IconSystem.chevronRight size={14} />
        </button>
      </div>

      {/* Calendar table */}
      {!calData || calData.categories.length === 0 ? (
        <div className="avail-cal-empty">{loading ? 'Loading availability...' : 'No availability data'}</div>
      ) : (
        <div className={`avail-cal-wrapper${loading ? ' avail-cal-loading' : ''}`}>
          <table className="avail-cal-table">
            <thead>
              <tr>
                <th className="avail-cal-cat-cell" style={{ borderRight: '2px solid #475569', borderBottom: '2px solid #334155' }}>Category</th>
                {dateStrings.map((ds, i) => {
                  const hdr = formatHeaderLabel(ds);
                  const classes = [
                    ds === todayStr && 'today',
                    inRangeSet.has(ds) && 'in-range',
                  ].filter(Boolean).join(' ');
                  const isLast = i === dateStrings.length - 1;
                  return (
                    <th key={ds} className={classes || undefined} style={{ borderRight: isLast ? 'none' : '1px solid rgba(255,255,255,0.15)', borderBottom: '2px solid #334155' }}>
                      <span className="day-name">{hdr.dayName}</span>
                      <span className="day-num">{hdr.dateLabel}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {calData.categories.map(cat => {
                const catDays = calData.dayMap.get(cat.roomCategoryId);
                return (
                  <tr key={cat.roomCategoryId}>
                    <td className="avail-cal-cat-cell" title={cat.categoryName} style={{ borderRight: '2px solid #94a3b8', borderBottom: CELL_BORDER }}>
                      {cat.categoryCode} <span style={{ fontWeight: 400, opacity: 0.7 }}>({cat.totalRooms})</span>
                    </td>
                    {dateStrings.map((ds, i) => {
                      const day = catDays?.get(ds);
                      const avail = day ? day.availableRooms : cat.totalRooms;
                      const booked = cat.totalRooms - avail;
                      const occPct = cat.totalRooms > 0 ? Math.round((booked / cat.totalRooms) * 100) : 0;
                      const badge = getOccupancyBadgeClass(booked, cat.totalRooms);
                      const cellClasses = [
                        'avail-cal-cell',
                        badge,
                        inRangeSet.has(ds) && 'in-range',
                      ].filter(Boolean).join(' ');
                      const isLast = i === dateStrings.length - 1;
                      const cellStyle = isLast
                        ? { ...CELL_COLORS[badge], borderRight: 'none' }
                        : CELL_COLORS[badge];
                      return (
                        <td
                          key={ds}
                          className={cellClasses}
                          style={cellStyle}
                          title={`${cat.categoryName}: ${avail}/${cat.totalRooms} available · ${occPct}% occupancy`}
                        >
                          {avail}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>

            {/* House-level footer — the desk's question is "how full is
                Thursday?", not per-category sellability. Numbers are plain sums
                of the same cells above, so the two views can never disagree. */}
            {showTotals && (() => {
              const houseTotal = calData.categories.reduce((a, c) => a + c.totalRooms, 0);
              const dayTotals = dateStrings.map(ds => calData.categories.reduce((a, c) => {
                const day = calData.dayMap.get(c.roomCategoryId)?.get(ds);
                return a + (day ? day.availableRooms : c.totalRooms);
              }, 0));
              return (
                <tfoot>
                  <tr>
                    <td className="avail-cal-cat-cell" style={{ borderRight: '2px solid #94a3b8', borderTop: '2px solid #334155' }}>
                      Total free <span style={{ fontWeight: 400, opacity: 0.7 }}>({houseTotal})</span>
                    </td>
                    {dayTotals.map((free, i) => (
                      <td
                        key={dateStrings[i]}
                        className="avail-cal-cell"
                        style={{ borderTop: '2px solid #334155', borderRight: i === dayTotals.length - 1 ? 'none' : CELL_BORDER, fontWeight: 700 }}
                        title={`${free} of ${houseTotal} rooms free`}
                      >
                        {free}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="avail-cal-cat-cell" style={{ borderRight: '2px solid #94a3b8', fontWeight: 400, opacity: 0.8 }}>
                      Occupancy
                    </td>
                    {dayTotals.map((free, i) => {
                      const occPct = houseTotal > 0 ? Math.round(((houseTotal - free) / houseTotal) * 100) : 0;
                      return (
                        <td
                          key={dateStrings[i]}
                          className="avail-cal-cell"
                          style={{ borderRight: i === dayTotals.length - 1 ? 'none' : CELL_BORDER, fontSize: '0.68rem', opacity: 0.85 }}
                        >
                          {occPct}%
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      )}

      {/* Legend — bands by occupancy % */}
      <div className="avail-cal-legend">
        {([
          ['high', 'Empty (0%)'],
          ['medium', 'Low (1–60%)'],
          ['low', 'Busy (61–84%)'],
          ['critical', 'Near full (85–99%)'],
          ['zero', 'Sold out (100%)'],
        ] as const).map(([key, label]) => (
          <span key={key} className="avail-cal-legend-item">
            <span className="avail-cal-legend-dot" style={{ backgroundColor: CELL_COLORS[key].backgroundColor as string }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default AvailabilityCalendarPanel;
